import { OrderSupport } from './OrderSupport';
import { useCheckoutRefresh } from './useCheckoutRefresh';
import { useEffect, useRef, useState } from 'react';
import { useData, post, useSession, orgID } from '@yingbaba/api';
import { Button, ButtonLink, ErrorNotice, Modal, Notice } from '@yingbaba/ui';
import {
  datetime,
  money,
  memberCommandKey,
  clearMemberCommand,
  digitalProjectAssetPath,
} from '@yingbaba/utils';
import type { DigitalCheckoutView, DigitalOrderView, MemberPaymentMethod } from '@yingbaba/types';

const paymentStates: Record<string, string> = {
  creating: '正在准备支付',
  pending: '等待支付',
  unknown: '支付结果待确认',
  closing: '正在确认关单',
  closed: '支付单已关闭',
  paid: '渠道已确认到账',
};
function safePaymentURL(raw: string, provider: string): string | undefined {
  try {
    const u = new URL(raw);
    if (u.protocol !== 'https:' || u.username || u.password || u.port) return;
    if (provider === 'wechat' && ['wx.tenpay.com', 'payapp.weixin.qq.com'].includes(u.hostname))
      return raw;
    if (
      provider === 'alipay' &&
      (u.hostname === 'alipay.com' ||
        u.hostname.endsWith('.alipay.com') ||
        u.hostname.endsWith('.alipaydev.com'))
    )
      return raw;
  } catch {
    /* Only authenticated provider links are launchable. */
  }
}

type CheckoutProps = {
  orderID?: string;
  paymentID?: string;
  onClose: () => void;
  onChanged: () => void;
  onViewPurchase: (order: DigitalOrderView) => void;
};
export function DigitalCheckout(props: CheckoutProps) {
  const session = useSession();
  return (
    <DigitalCheckoutSession
      key={`${session.data?.account.id}:${orgID()}:${props.orderID || ''}:${props.paymentID || ''}`}
      {...props}
    />
  );
}
function DigitalCheckoutSession({
  orderID,
  paymentID,
  onClose,
  onChanged,
  onViewPurchase,
}: CheckoutProps) {
  const session = useSession();
  const path = paymentID ? `/digital/payments/${paymentID}` : `/digital/orders/${orderID}/checkout`;
  const q = useData<DigitalCheckoutView>(path, !!(orderID || paymentID));
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [stopped, setStopped] = useState(false);
  const pending = useRef(false);
  const observed = useRef('');
  const alive = useRef(true);
  const view = !stopped && !q.isError && q.data?.order ? q.data : undefined;
  const p = view?.payment;
  const {
    refresh: refreshPayment,
    refreshing,
    needsQuery,
  } = useCheckoutRefresh({
    kind: 'digital',
    payment: p,
    receiptStatus: view?.receipt?.fulfillment_status,
    orderStatus: view?.order.status,
    stopped,
    busy,
    refetch: q.refetch,
    onError: setError,
    onStopped: () => setStopped(true),
  });
  const ownerName =
    view?.order.owner_type === 'account'
      ? session.data?.account.name
      : session.data?.organizations.find((o) => o.id === view?.order.owner_id)?.name;
  let href =
    p?.checkout_method === 'GET' ? safePaymentURL(p.checkout_url || '', p.provider) : undefined;
  if (href && p?.provider === 'wechat' && p.channel === 'h5') {
    const destination = new URL(href);
    const back = new URL('/digital-resources', window.location.origin);
    back.searchParams.set('digital_payment', p.merchant_order_no);
    destination.searchParams.set('redirect_url', back.toString());
    href = destination.toString();
  }
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  useEffect(() => {
    if (!view) return;
    const state = `${view.order.status}:${view.receipt?.fulfillment_status || ''}`;
    if (state !== observed.current) {
      observed.current = state;
      if (['paid', 'payment_review', 'refunded', 'cancelled'].includes(view.order.status))
        onChanged();
    }
  }, [view?.order.status, view?.receipt?.fulfillment_status]);

  async function start(method: MemberPaymentMethod) {
    if (!view?.can_pay || pending.current) return;
    pending.current = true;
    setBusy(true);
    setError(null);
    const payload = { provider: method.provider, channel: method.channel };
    const scope = `digital-payment:${session.data?.account.id}:${view.order.id}`;
    try {
      const idempotency_key = await memberCommandKey(scope, payload);
      if (!alive.current) return;
      await post<DigitalCheckoutView>(`/digital/orders/${view.order.id}/payments`, {
        ...payload,
        idempotency_key,
      });
      clearMemberCommand(scope);
      if (alive.current) {
        await q.refetch();
        if (alive.current) onChanged();
      }
    } catch (e) {
      if (alive.current) setError(e);
    } finally {
      pending.current = false;
      if (alive.current) setBusy(false);
    }
  }
  async function cancel() {
    if (!view?.can_cancel || pending.current) return;
    pending.current = true;
    setBusy(true);
    setError(null);
    try {
      await post(`/digital/orders/${view.order.id}/cancel`);
      if (alive.current) {
        await q.refetch();
        if (alive.current) onChanged();
      }
    } catch (e) {
      if (alive.current) setError(e);
    } finally {
      pending.current = false;
      if (alive.current) setBusy(false);
    }
  }
  const isWeChatBrowser = /MicroMessenger/i.test(navigator.userAgent);
  return (
    <Modal
      open
      busy={busy}
      title="数字资源授权在线收银台"
      description="核对授权订单及支付状态，确认到账后可查看授权凭证。"
      onOpenChange={(open) => {
        if (!open && !busy) onClose();
      }}
    >
      <ErrorNotice error={q.error || error} />
      {q.isLoading ? (
        <p>正在读取订单和支付状态…</p>
      ) : !view ? (
        <div className="stack">
          <p>收银台暂时无法读取，订单会保留，请稍后重试。</p>
          <Button
            type="button"
            variant="secondary"
            disabled={stopped || refreshing}
            onClick={() => void refreshPayment()}
          >
            重新读取订单
          </Button>
        </div>
      ) : (
        <>
          <p>
            归属：{view.order.owner_type === 'account' ? '个人' : '组织'} ·{' '}
            {ownerName || view.order.owner_id}
          </p>
          <h3>
            {view.order.title} · {view.order.contract.license.name}
          </h3>
          <p style={{ fontSize: 26, fontWeight: 700 }}>{money(view.order.amount_minor)}</p>
          <p className="small muted" style={{ overflowWrap: 'anywhere' }}>
            订单号：{view.order.id}
          </p>
          {view.receipt?.fulfillment_status === 'fulfilled' ? (
            <section className="digital-checkout-complete" aria-label="授权已签发">
              <div>
                <strong>授权已签发</strong>
                <p className="muted small">
                  查看授权凭证、下载已购文件，或将素材用于许可约定的项目。
                </p>
              </div>
              <div className="row wrap">
                <Button onClick={() => onViewPurchase(view.order)}>查看授权与下载</Button>
                {view.order.owner_type === 'organization' &&
                  view.order.owner_id === orgID() &&
                  ['owner', 'admin'].includes(
                    session.data?.organizations.find((org) => org.id === orgID())?.role || '',
                  ) &&
                  view.order.project_id && (
                    <ButtonLink
                      variant="secondary"
                      to={digitalProjectAssetPath(view.order.project_id, view.order.id)}
                    >
                      前往项目使用
                    </ButtonLink>
                  )}
              </div>
            </section>
          ) : view.receipt?.fulfillment_status === 'refunded' ? (
            <Notice>这笔到账已登记全额退款，对应下载许可已停止；其他独立订单不受影响。</Notice>
          ) : view.receipt ? (
            <Notice>
              渠道已确认到账，数字资源授权开通
              {view.receipt.fulfillment_status === 'pending' ? '处理中' : '需要平台核查'}
              。请勿重复付款。流水：{view.receipt.trade_no}
            </Notice>
          ) : view.order.status === 'cancelled' ? (
            <Notice>订单已取消；有在线支付时，渠道关单已确认。需要购买请重新提交申请。</Notice>
          ) : p ? (
            <>
              <p role="status">{paymentStates[p.status] || p.status}</p>
              <p>支付有效至：{datetime(p.expires_at)}</p>
              {p.qr_image && /^data:image\/png;base64,[A-Za-z0-9+/=]+$/.test(p.qr_image) && (
                <div style={{ textAlign: 'center' }}>
                  <img
                    src={p.qr_image}
                    alt={
                      p.provider === 'wechat'
                        ? '微信数字资源授权支付二维码'
                        : '支付宝数字资源授权支付二维码'
                    }
                    width={288}
                    height={288}
                    style={{ width: 'min(288px, 100%)', height: 'auto' }}
                  />
                  <p>请使用{p.provider === 'wechat' ? '微信' : '支付宝'}扫码支付。</p>
                </div>
              )}
              {href && (
                <a
                  className="btn btn-primary"
                  href={href}
                  target="_blank"
                  rel={p.provider === 'wechat' ? 'noopener' : 'noopener noreferrer'}
                  referrerPolicy={p.provider === 'wechat' ? 'origin' : 'no-referrer'}
                >
                  前往{p.provider === 'wechat' ? '微信' : '支付宝'}付款
                </a>
              )}
              {p.status === 'unknown' && (
                <Notice>
                  请求结果未知，系统会使用原支付单号继续核验。请勿重复转账或另外创建支付单。
                </Notice>
              )}
              {p.status === 'closing' && (
                <Notice>
                  已提交取消，正在核对渠道结果。关单确认前不会生成另一笔可支付订单；已支付的款项会进入到账处理。
                </Notice>
              )}
            </>
          ) : (
            <>
              <p>选择付款方式</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                {view.methods
                  .filter((m) => m.available)
                  .map((m) => (
                    <Button
                      key={`${m.provider}:${m.channel}`}
                      disabled={
                        busy ||
                        !view.can_pay ||
                        (m.provider === 'wechat' && m.channel === 'h5' && isWeChatBrowser)
                      }
                      onClick={() => void start(m)}
                    >
                      {m.name}
                    </Button>
                  ))}
              </div>
              {!view.methods.some((m) => m.available) && (
                <div className="stack">
                  <Notice>暂未开放在线付款，请先联系平台确认付款方式，再完成授权购买。</Notice>
                  <Button type="button" variant="secondary" onClick={() => void q.refetch()}>
                    刷新支付方式
                  </Button>
                </div>
              )}
              {isWeChatBrowser && (
                <p className="small muted">此网页支付入口需在微信外的手机浏览器打开。</p>
              )}
            </>
          )}
          {(needsQuery ||
            (view.receipt &&
              !['fulfilled', 'refunded'].includes(view.receipt.fulfillment_status))) && (
            <div className="stack" role="status" aria-live="polite">
              {view.receipt?.fulfillment_status === 'pending' && (
                <p className="small muted">正在更新授权状态，你可以稍后在“我的购买”查看结果。</p>
              )}
              <Button
                type="button"
                variant="secondary"
                disabled={busy || stopped || refreshing}
                onClick={() => void refreshPayment()}
              >
                {refreshing ? '正在刷新…' : view.receipt ? '刷新授权状态' : '刷新支付结果'}
              </Button>
            </div>
          )}
          <p className="small muted">
            本次购买不会自动续费。付款确认后，可查看授权凭证并下载资源。
          </p>
          <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
            {view.can_cancel && !p?.cancel_requested && (
              <Button variant="secondary" disabled={busy} onClick={() => void cancel()}>
                取消订单
              </Button>
            )}
            <OrderSupport kind="digital" orderID={view.order.id} disabled={busy} />
            <Button variant="secondary" disabled={busy} onClick={onClose}>
              关闭收银台
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
}
